/**
 * ConsequenceModal.jsx — Explain stage of the Predict→Observe→Explain loop.
 * Shown after a wrong-answer consequence plays out in slow motion.
 *
 * The student must EXPLAIN why the crash happened (misconception-tagged
 * multiple choice) before they can retry or continue. The correct speed
 * range is only revealed after a correct explanation — understanding
 * unlocks the answer.
 */
import { useState, useEffect } from "react";
import { useGame, MODES } from "./store";
import { getExplainQuestion } from "./questionBank";

export default function ConsequenceModal() {
  const consequencePreview = useGame((s) => s.consequencePreview);
  const slowMotion = useGame((s) => s.slowMotion);
  const savedDecision = useGame((s) => s.savedDecision);
  const savedSpeed = useGame((s) => s.savedSpeed);
  const savedCorrectAnswer = useGame((s) => s.savedCorrectAnswer);
  const savedPos = useGame((s) => s.savedPos);
  const savedSeg = useGame((s) => s.savedSeg);
  const savedS = useGame((s) => s.savedS);
  const savedV = useGame((s) => s.savedV);
  const mode = useGame((s) => s.mode);

  const [explainQ, setExplainQ] = useState(null);
  const [pickedWrong, setPickedWrong] = useState([]);
  const [explainFeedback, setExplainFeedback] = useState(null);
  const explained = !!explainFeedback?.ok;

  // Only show when consequence has landed (slowMotion off, consequencePreview on)
  const visible =
    consequencePreview &&
    !slowMotion &&
    (mode === MODES.ON_GROUND || mode === MODES.IN_WATER);

  useEffect(() => {
    if (visible && savedDecision) {
      setExplainQ(getExplainQuestion(savedDecision.kind));
      setPickedWrong([]);
      setExplainFeedback(null);
    }
  }, [visible, savedDecision]);

  if (!visible || !savedDecision) return null;

  const pickExplanation = (opt) => {
    if (pickedWrong.includes(opt.id) || explained) return;
    if (opt.correct) {
      setExplainFeedback({
        ok: true,
        msg: "Exactly right. Now you know WHY — try again with that understanding.",
      });
    } else {
      useGame.getState().logMisconception(opt.tag);
      setPickedWrong((p) => [...p, opt.id]);
      setExplainFeedback({ ok: false, msg: opt.whyWrong });
    }
  };

  const handleContinue = () => {
    useGame.setState({
      consequencePreview: false,
      savedDecision: null,
    });
  };

  const handleRetry = () => {
    const st = useGame.getState();
    // For hazard crashes: if the student already solved v_max, respawn DRIVING
    // (the HUD shows their target). If not, re-show the question at the checkpoint.
    // Challenge mode never asks; Phase-2 events re-ask via their own triggers.
    const needQuestion =
      !st.challengeGoal &&
      !!savedDecision.hazardId &&
      st.hazardVmax[savedDecision.hazardId] == null;

    useGame.setState({
      consequencePreview: false,
      slowMotion: false,
      mode: MODES.ON_TRACK,
      // GameLoop teleports the cart back via the respawn channel
      respawnS: savedS,
      respawnV: savedV ?? 4,
      paused: needQuestion,
      decision: needQuestion
        ? {
            kind: "curve",
            label: `⚠ ${savedDecision.label} — find the safe speed!`,
            hazardId: savedDecision.hazardId,
          }
        : null,
      decisionAttempts: 1,
      greenSinceMs: 0,
      // Geometry for the re-shown question (binding radius of the hazard)
      ...(needQuestion && savedDecision.rMin != null
        ? { upcomingRadius: savedDecision.rMin, upcomingSlope: savedDecision.slope || 0 }
        : {}),
      retryPrefill: needQuestion ? savedSpeed : null,
      pos: savedPos,
      s: savedS,
      v: savedV,
      savedDecision: null,
    });
  };

  const wrongSpeed = typeof savedSpeed === "number" ? savedSpeed.toFixed(1) : "?";
  const correctSpeed =
    typeof savedCorrectAnswer === "number"
      ? savedCorrectAnswer.toFixed(1)
      : "?";

  return (
    <div
      data-testid="consequence-modal"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(2,4,12,0.88)",
        backdropFilter: "blur(12px)",
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          width: "min(680px, 100%)",
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          background: "linear-gradient(160deg, #1a0d2e 0%, #0a0c1e 100%)",
          border: `2px solid ${explained ? "#00ffaa" : "#ff4d6d"}`,
          borderRadius: 22,
          padding: 32,
          color: "#fff",
          boxShadow: `0 24px 80px ${explained ? "rgba(0,255,170,0.25)" : "rgba(255,77,109,0.25)"}`,
          transition: "all 0.3s",
        }}
      >
        {/* Header */}
        <div
          style={{
            fontSize: 11,
            letterSpacing: 3,
            color: explained ? "#00ffaa" : "#ff4d6d",
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          {explained ? "✓ EXPLAINED" : "⚠ CONSEQUENCE OBSERVED — NOW EXPLAIN IT"}
        </div>
        <h2 style={{ margin: "0 0 16px", fontSize: 22, lineHeight: 1.3 }}>
          You saw what happened at {wrongSpeed} m/s.
        </h2>

        {/* ─── EXPLAIN QUESTION ─── */}
        {explainQ && (
          <div data-testid="explain-stage" style={{ marginBottom: 20 }}>
            <p style={{ color: "#cfd6e3", fontSize: 15, lineHeight: 1.6, fontWeight: 600 }}>
              {explainQ.question}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {explainQ.options.map((opt) => {
                const wrong = pickedWrong.includes(opt.id);
                const isCorrectPick = explained && opt.correct;
                return (
                  <button
                    key={opt.id}
                    data-testid={`explain-option-${opt.id}`}
                    onClick={() => pickExplanation(opt)}
                    disabled={wrong || explained}
                    style={{
                      textAlign: "left",
                      padding: "12px 16px",
                      borderRadius: 12,
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      fontFamily: "inherit",
                      cursor: wrong || explained ? "default" : "pointer",
                      background: isCorrectPick
                        ? "rgba(0,255,170,0.15)"
                        : wrong
                          ? "rgba(255,77,109,0.08)"
                          : "rgba(255,255,255,0.05)",
                      border: isCorrectPick
                        ? "1px solid rgba(0,255,170,0.6)"
                        : wrong
                          ? "1px solid rgba(255,77,109,0.4)"
                          : "1px solid rgba(255,255,255,0.15)",
                      color: isCorrectPick ? "#00ffaa" : wrong ? "#7a6470" : "#e8ecf3",
                      textDecoration: wrong ? "line-through" : "none",
                      opacity: wrong ? 0.65 : 1,
                      transition: "all 0.2s",
                    }}
                  >
                    {opt.text}
                  </button>
                );
              })}
            </div>

            {explainFeedback && (
              <div
                data-testid="explain-feedback"
                style={{
                  marginTop: 14,
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: explainFeedback.ok
                    ? "rgba(0,255,170,0.18)"
                    : "rgba(255,77,109,0.12)",
                  color: explainFeedback.ok ? "#00ffaa" : "#ff8a9b",
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  fontWeight: 500,
                  border: `1px solid ${explainFeedback.ok ? "rgba(0,255,170,0.5)" : "rgba(255,77,109,0.35)"}`,
                }}
              >
                {explainFeedback.ok ? "✓ " : "✗ Not quite. "}
                {explainFeedback.msg}
              </div>
            )}
          </div>
        )}

        {/* Values summary — correct range revealed only after explanation */}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              flex: 1,
              background: "rgba(255,77,109,0.12)",
              borderRadius: 10,
              padding: "10px 14px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 11, color: "#9aa4b2", marginBottom: 4 }}>
              YOUR VALUE
            </div>
            <div
              style={{ fontSize: 22, fontWeight: 700, color: "#ff4d6d" }}
            >
              {wrongSpeed} m/s
            </div>
          </div>
          <div
            style={{
              flex: 1,
              background: "rgba(0,255,170,0.08)",
              borderRadius: 10,
              padding: "10px 14px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 11, color: "#9aa4b2", marginBottom: 4 }}>
              {savedDecision.kind === "brake" ? "NEEDED DISTANCE" : "CORRECT RANGE"}
            </div>
            <div
              style={{ fontSize: 22, fontWeight: 700, color: explained ? "#00ffaa" : "#5a6478" }}
            >
              {explained
                ? `≈ ${correctSpeed} ${savedDecision.kind === "brake" ? "m" : "m/s"}`
                : "🔒 explain first"}
            </div>
          </div>
        </div>

        {/* Action buttons — locked until the student explains the crash */}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            data-testid="consequence-continue"
            onClick={handleContinue}
            disabled={!explained}
            style={{
              flex: 1,
              padding: "14px 16px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 12,
              color: explained ? "#cfd6e3" : "#5a6478",
              fontSize: 14,
              fontWeight: 600,
              cursor: explained ? "pointer" : "not-allowed",
              opacity: explained ? 1 : 0.5,
              fontFamily: "inherit",
              transition: "all 0.2s",
            }}
          >
            Continue from here (press C anytime to climb back to the track)
          </button>
          <button
            data-testid="consequence-retry"
            onClick={handleRetry}
            disabled={!explained}
            style={{
              flex: 1,
              padding: "14px 16px",
              background: explained
                ? "linear-gradient(135deg, #ffd23f, #ff8a3d)"
                : "rgba(255,255,255,0.08)",
              border: "none",
              borderRadius: 12,
              color: explained ? "#1a0d00" : "#5a6478",
              fontSize: 14,
              fontWeight: 700,
              cursor: explained ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              transition: "all 0.2s",
            }}
          >
            {explained ? "Try again with correct speed!" : "🔒 Explain the crash first"}
          </button>
        </div>
      </div>
    </div>
  );
}
